import packageJson from '../../../package.json';

export const WEB_APP_ID = 'com.paragonixtechnologies.app';
export const WEB_DEVICE_ID_COOKIE_NAME = 'pgx-device-id';
export const WEB_TIMEZONE_COOKIE_NAME = 'pgx-timezone';

type WebDeviceMeta = {
  device: string;
  os: string;
  osVersion: string;
};

type WebLocaleMeta = {
  language: string;
  locale: string;
};

const DEFAULT_LANGUAGE = 'en';
const DEFAULT_LOCALE = 'en-US';

export function parseWebUserAgent(userAgent?: string | null): WebDeviceMeta {
  const ua = userAgent ?? '';

  let os = 'Unknown';
  let osVersion = 'Unknown';

  if (ua.includes('iPhone') || ua.includes('iPad') || ua.includes('iPod')) {
    os = 'iOS';
    osVersion = ua.match(/OS ([\d_]+)/)?.[1]?.replace(/_/g, '.') ?? 'Unknown';
  } else if (ua.includes('Android')) {
    os = 'Android';
    osVersion = ua.match(/Android ([\d.]+)/)?.[1] ?? 'Unknown';
  } else if (ua.includes('Windows NT')) {
    os = 'Windows';
    const windowsVersion = ua.match(/Windows NT ([\d.]+)/)?.[1];
    const ntVersionMap: Record<string, string> = {
      '10.0': '10/11',
      '6.3': '8.1',
      '6.2': '8',
      '6.1': '7',
      '6.0': 'Vista',
    };
    osVersion = (windowsVersion && ntVersionMap[windowsVersion]) || windowsVersion || 'Unknown';
  } else if (ua.includes('Mac OS X')) {
    os = 'macOS';
    osVersion = ua.match(/Mac OS X ([\d_]+)/)?.[1]?.replace(/_/g, '.') ?? 'Unknown';
  } else if (ua.includes('Linux')) {
    os = 'Linux';
  }

  return {
    device: formatBrowserDeviceLabel(getBrowserFamily(ua)),
    os,
    osVersion,
  };
}

export function parsePreferredLocale(value?: string | null): WebLocaleMeta {
  const primaryValue = value?.split(',')[0]?.trim().replace(/_/g, '-') || DEFAULT_LOCALE;
  const [rawLanguage, rawRegion] = primaryValue.split('-');

  const language = rawLanguage?.toLowerCase() || DEFAULT_LANGUAGE;
  const locale = rawRegion ? `${language}-${rawRegion.toUpperCase()}` : inferLocaleFromLanguage(language);

  return { language, locale };
}

export function resolveWebAppVersion(appVersion?: string | null): string {
  return appVersion?.trim() || packageJson.version || 'Unknown';
}

function getBrowserFamily(userAgent: string): string {
  if (userAgent.includes('Edg/')) {
    return 'Edge';
  }

  if (userAgent.includes('Firefox/')) {
    return 'Firefox';
  }

  if (userAgent.includes('Chrome/') && !userAgent.includes('Edg/')) {
    return 'Chrome';
  }

  if (userAgent.includes('Safari/') && !userAgent.includes('Chrome/')) {
    return 'Safari';
  }

  return 'Unknown';
}

function formatBrowserDeviceLabel(browserFamily: string): string {
  return browserFamily === 'Unknown' ? 'Web Browser' : `Web Browser (${browserFamily})`;
}

function inferLocaleFromLanguage(language: string): string {
  if (language === DEFAULT_LANGUAGE) {
    return DEFAULT_LOCALE;
  }

  return `${language}-${language.toUpperCase()}`;
}
